import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ChevronRight, ExternalLink, FileText } from 'lucide-react';
import { AUReadinessReportAssessment } from '@/components/tools/AUReadinessReportAssessment';
import { TOOLS } from '@sizzlenz/shared';
import { useToolAccessGuard } from '@/lib/hooks/useToolAccessGuard';
const GLOWPATH_EXTERNAL_URL = 'https://www.goglowpath.com/';
const GLOWPATH_INTERNAL_HREF = '/entities/glowpath';
interface GlowpathLinkProps {
  variant?: 'external' | 'internal';
  className?: string;
  children: React.ReactNode;
}
function GlowpathLink(t0) {
  const $ = _c(6);
  const {
    variant: t1,
    className,
    children
  } = t0;
  const variant = t1 === undefined ? "internal" : t1;
  if (variant === "external") {
    let t2;
    if ($[0] !== children || $[1] !== className) {
      t2 = <a href={GLOWPATH_EXTERNAL_URL} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>;
      $[0] = children;
      $[1] = className;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== children || $[4] !== className) {
    t2 = <Link href={GLOWPATH_INTERNAL_HREF} className={className}>{children}</Link>;
    $[3] = children;
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export default function AURetailReadinessReportPage() {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = TOOLS.find(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tool = t0;
  const router = useRouter();
  const {
    allowed,
    loading
  } = useToolAccessGuard("/tools/au-retail-readiness-report");
  const [isEmbed, setIsEmbed] = useState(_temp2);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsEmbed(new URLSearchParams(window.location.search).has("embed"));
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== allowed || $[4] !== isEmbed || $[5] !== loading || $[6] !== router) {
    t3 = () => {
      if (!loading && !allowed && !isEmbed) {
        router.replace("/tools");
      }
    };
    t4 = [loading, allowed, isEmbed, router];
    $[3] = allowed;
    $[4] = isEmbed;
    $[5] = loading;
    $[6] = router;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  if (loading || !allowed && !isEmbed) {
    return null;
  }
  if (isEmbed) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Head><title>GLOWPATH Australian Retail Readiness Snapshot</title><meta name="description" content="Interactive GLOWPATH AU Retail Readiness Snapshot - learn what buyers actually evaluate, why brands get declined, and assess your brand against real buyer data." /></Head>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbedHeightReporter />;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <>{t5}<div className="bg-white px-4 py-6">{t6}<div id="embed-content"><AUReadinessReportAssessment /></div></div></>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Head><title>GLOWPATH Australian Retail Readiness Snapshot | Sizzle</title><meta name="description" content="Interactive GLOWPATH AU Retail Readiness Snapshot - learn what buyers actually evaluate, why brands get declined, and assess your brand against real buyer data." /></Head>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6"><Link href="/tools" className="hover:text-indigo-600 transition-colors cursor-pointer">Tools</Link><ChevronRight className="w-3.5 h-3.5" /><span className="text-gray-900 font-medium">GLOWPATH Australian Retail Readiness Snapshot</span></nav>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-800"><span>A report from</span><GlowpathLink className="inline-flex items-center gap-1 font-bold text-indigo-700 underline underline-offset-2 hover:text-indigo-900 cursor-pointer">GLOWPATH</GlowpathLink></div>;
    t8 = <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Australian Retail Readiness Snapshot</h1>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 border-b border-gray-100"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">{t6}{t7}{t8}<p className="mt-3 text-lg text-gray-600 max-w-2xl">An interactive walkthrough of the 2026{" "}<GlowpathLink className="text-indigo-600 font-medium hover:text-indigo-800 underline underline-offset-2 cursor-pointer">GLOWPATH</GlowpathLink>{" "}Australian Retail Readiness Snapshot. Learn what buyers actually evaluate, why brands get declined, and assess your brand against real data from AU retail buyers with active ranging authority.</p>{tool?.sourceUrl && <a href={tool.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2 cursor-pointer"><FileText className="w-4 h-4" />{tool.sourceLabel || "View original source"}<ExternalLink className="w-3.5 h-3.5" /></a>}</div></div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"><AUReadinessReportAssessment /></div>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="text-sm text-gray-600">This report is produced by{" "}<GlowpathLink className="font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2 cursor-pointer">GLOWPATH</GlowpathLink>{" "}— helping brands navigate retail readiness in Australia.</p>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <>{t5}<div className="min-h-screen bg-white">{t9}{t10}<div className="border-t border-gray-100 bg-gray-50"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">{t11}<GlowpathLink variant="external" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors cursor-pointer">Visit GLOWPATH<ExternalLink className="w-3.5 h-3.5" /></GlowpathLink></div></div></div></>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}
function _temp2() {
  if (typeof window !== "undefined") {
    return new URLSearchParams(window.location.search).has("embed");
  }
  return false;
}
function _temp(t) {
  return t.href === "/tools/au-retail-readiness-report";
}
function EmbedHeightReporter() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp3, t0);
  return null;
}
function _temp3() {
  const isInIframe = window.self !== window.top;
  if (!isInIframe) {
    return;
  }
  const content = document.getElementById("embed-content");
  if (!content) {
    return;
  }
  let lastHeight = 0;
  const reportHeight = function reportHeight() {
    const height = content.offsetHeight + 48;
    if (Math.abs(height - lastHeight) > 5) {
      lastHeight = height;
      window.parent.postMessage({
        type: "embed-resize",
        height
      }, "*");
    }
  };
  const observer = new ResizeObserver(reportHeight);
  observer.observe(content);
  reportHeight();
  return () => {
    observer.disconnect();
  };
}